import React, { useEffect, useRef, useState } from 'react';
import withSimpleBar from '../hoc/withSimpleBar';
import {
  Avatars,
  Button,
  FleetaLogoOnly,
  IconButton,
  Tooltip,
  Typography,
} from '@thingsw/pitta-design-system';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import CheckIcon from '@material-ui/icons/Check';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { LightColors, Webviewer } from '@thingsw/pitta-modules';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../features/store';
import { loadUserProfile, USER } from '../features/User/slice';
import { ProfileMenu } from '../components/ProfileMenu';
import { useTranslation } from 'react-i18next';
import { makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core';
import { THEME } from '../features/Theme/slice';
import clsx from 'clsx';
import { getFreetrialState, PAYMENT } from '../features/Payment/slice';

const useStyle = makeStyles((theme: Theme) => ({
  headerDiv: {
    width: '100%',
    height: 56,
    borderBottom: (props: any) => `1px solid ${props.colors.primary['6']}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0px 24px',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    justifyContent: 'start',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    maxWidth: 1052,
    margin: '48px auto 12px',
  },
  featureCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 16,
    cursor: 'pointer',
    border: (props: any) => `1px solid ${props.colors.primary['6']}`,
    borderRadius: 4,
  },
  featureCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flex: 1,
  },
  mobileFeatureCard: {
    display: 'flex',
    width: 'calc(100% - 24px)',
    flexDirection: 'column',
    gap: 8,
    borderRadius: 4,
    padding: '16px 12px',
    margin: '0px 12px',
    border: (props: any) => `1px solid ${props.colors.primary['6']}`,
  },
  selectedFeatureCard: {
    backgroundColor: (props: any) => props.colors.primary['1'],
    '& span': {
      color: (props: any) => `${props.colors.primary['0']} !important
      `,
    },
  },
  textBox: {
    padding: '10px 0px',
  },
}));

const featureItems = [
  {
    title: 'Geo Fencing',
    content: 'Create geofences and_',
    image: '/images/features_geofence.png',
  },
  {
    title: 'Reports',
    content: 'View at a_',
    image: '/images/features_reports.png',
  },
  {
    title: 'Multi Live View',
    content: 'Monitor up to_',
    image: '/images/features_multi_live_view.png',
  },
];

const NeedPaymentScreen = withSimpleBar(() => {
  const { color, colors } = useSelector((state: RootState) => state[THEME]);
  const classes = useStyle({ color, colors });
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const theme = useTheme() as Theme;
  const mobile = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  const profileAnchorRef = useRef<HTMLDivElement>(null);
  const state = location.state as any;
  const { userProfile } = useSelector((state: RootState) => state[USER]);
  const [openProfileMenu, setOpenProfileMenu] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(0);
  const [step, setStep] = useState(0);
  const { freetrial } = useSelector((state: RootState) => state[PAYMENT]);

  useEffect(() => {
    dispatch(getFreetrialState());
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadUserProfile());
  }, [dispatch]);

  const handleMoveToSubscription = () => {
    setStep(1);
  };

  const handleMoveToBilling = () => {
    history.push('/billing', {
      mode: state?.mode,
      fleetaPromo: !freetrial?.trialUsed,
    });
  };

  const onClickPrevFeature = () => {
    setSelectedFeature(
      (prev) => (prev - 1 + featureItems.length) % featureItems.length
    );
  };

  const onClickNextFeature = () => {
    setSelectedFeature((prev) => (prev + 1) % featureItems.length);
  };

  const FeatureCardMarkup = ({
    title,
    content,
    isSelected,
    onClick,
  }: {
    title: string;
    content: string;
    isSelected: boolean;
    onClick?: () => void;
  }) => {
    return (
      <li
        onClick={onClick}
        className={clsx(classes.featureCard, {
          [classes.selectedFeatureCard]: isSelected,
        })}
      >
        <Typography variant='H6' htmlColor={LightColors.primary['2']}>
          {t(title)}
        </Typography>
        <Typography variant='Body' htmlColor={LightColors.primary['2']}>
          {t(content)}
        </Typography>
      </li>
    );
  };

  const MobileFeatureCardMarkup = ({
    title,
    content,
    onClickPrev,
    onClickNext,
  }: {
    title: string;
    content: string;
    onClickPrev?: () => void;
    onClickNext?: () => void;
  }) => {
    return (
      <div
        style={{
          width: '100%',
        }}
      >
        <div className={classes.mobileFeatureCard}>
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant='H6'>{t(title)}</Typography>
            <div>
              <IconButton>
                <ChevronLeftIcon
                  onClick={onClickPrev}
                  htmlColor={LightColors.primary['4']}
                />
              </IconButton>
              <IconButton>
                <ChevronRightIcon
                  onClick={onClickNext}
                  htmlColor={LightColors.primary['4']}
                />
              </IconButton>
            </div>
          </div>
          <div>
            <Typography variant='Body'>{t(content)}</Typography>
          </div>
        </div>
      </div>
    );
  };

  const webMarkup = () => {
    switch (step) {
      case 0:
        return (
          <>
            <div>
              <Typography variant='H1'>{t('Unique Fleet_')}</Typography>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ul className={classes.featureCardList}>
                {featureItems.map((feature, index) => (
                  <FeatureCardMarkup
                    key={feature.title}
                    title={feature.title}
                    content={feature.content}
                    isSelected={selectedFeature === index}
                    onClick={() => setSelectedFeature(index)}
                  />
                ))}
              </ul>
              <div
                style={{
                  flex: 2.7,
                }}
              >
                <img
                  style={{
                    objectFit: 'contain',
                    maxHeight: 460,
                  }}
                  src={featureItems[selectedFeature].image}
                  alt={featureItems[selectedFeature].title}
                />
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                paddingRight: 52,
              }}
            >
              <Typography variant='Body'>{t('Want to unlock_')}</Typography>
              <Button
                color='primary'
                style={{
                  width: 96,
                  height: 48,
                }}
                onClick={handleMoveToSubscription}
                variant='contained'
              >
                <Typography
                  style={{
                    whiteSpace: 'nowrap',
                  }}
                  variant='SmallBold'
                >
                  {t('Start Now')}
                </Typography>
              </Button>
            </div>
          </>
        );
      case 1:
        return (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                }}
              >
                <Typography variant='H1'>{t('Fleet')}</Typography>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant='Small'>
                    {t(
                      'FLEETA Tracking exclusive features for power users and businesses alike'
                    )}
                  </Typography>
                  <Typography variant='Small'>
                    {t('(Multiple users and cameras, geofencing, etc.).')}
                  </Typography>
                </div>
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='H6'>{t('$15.99')}</Typography>
                  <Typography variant='Caption'>{t('/ month')}</Typography>
                </div>
                <div>
                  <Typography
                    variant='Small'
                    htmlColor={LightColors.primary['2']}
                  >
                    {t('12 USD for extra Cloud Camera')}
                  </Typography>
                </div>
              </div>
            </div>
            <div
              style={{
                borderRadius: 16,
                position: 'relative',
                border: `2px solid ${colors.primary['6']}`,
                width: '100%',
                display: 'flex',
                boxShadow:
                  '0px 6px 40px 0px #0000000D, 0px 2px 2px 0px #0000000F',
              }}
            >
              <div
                style={{
                  flex: 1.5,
                  borderRight: `2px solid ${colors.primary['6']}`,
                  padding: '0 16px',
                }}
              >
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Cloud Cameras per Account')}
                  </Typography>
                </div>
                <div
                  style={{
                    padding: '20px 0',
                  }}
                >
                  <Typography variant='Body'>
                    {t('Users per Account')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Live View per Day')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Remote Video Playback_2')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Connect Dashcam Locally_')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>{t('Cloud Storage')}</Typography>
                </div>
                <div
                  className={classes.textBox}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>
                    {t('Live Event Upload')}
                  </Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('Uploads event_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>{t('Live GPS')}</Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Push Notifications_2')}
                  </Typography>
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  borderRight: `2px solid ${colors.primary['6']}`,
                  padding: '0 12px',
                }}
              >
                <div
                  style={{
                    padding: '10px 0 8px 0',
                  }}
                >
                  <Typography variant='BodyBold'>999</Typography>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant='BodyBold'>{t('Owner : 1')}</Typography>
                  <Typography variant='BodyBold'>{t('Admin : 10')}</Typography>
                  <Typography variant='BodyBold'>
                    {t('Driver : 3 per camera')}
                  </Typography>
                </div>
                <div
                  style={{
                    padding: '8px 0 8px 0',
                  }}
                >
                  <Typography variant='BodyBold'>{t('Unlimited')}</Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='BodyBold'>{t('Unlimited')}</Typography>
                </div>
                <div
                  style={{
                    padding: '38px 0 30px',
                  }}
                >
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <Typography variant='BodyBold'>
                    {t('15GB per Camera')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='BodyBold'>{t('30 days')}</Typography>
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
              </div>
              <div
                style={{
                  flex: 1.5,
                  borderRight: `2px solid ${colors.primary['6']}`,
                  padding: '0 16px',
                }}
              >
                <div
                  className={classes.textBox}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>{t('Voice Call')}</Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('Starts a conversation_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div
                  style={{
                    padding: '10px 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant='Body'>{t('Cloud Battery')}</Typography>
                    <Typography htmlColor={colors.primary['2']} variant='Body'>
                      **{t('Available with_')}
                    </Typography>
                  </div>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('Remotely check_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div
                  className={classes.textBox}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>
                    {t('Fatigue Risk Alert')}
                  </Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('Receive a voice_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>{t('Driving Reports')}</Typography>
                </div>
                <div
                  style={{
                    padding: '32px 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>{t('GPS Tracking')}</Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>{t('View your_')}</Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div
                  className={classes.textBox}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>{t('Video on Map')}</Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('View speed, event_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Geofence Setup (Web)_')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Multi Live View (Fleeta Web Viewer)')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Email Notifications')}
                  </Typography>
                </div>
              </div>
              <div
                style={{
                  flex: 0.5,
                  padding: '0 12px',
                }}
              >
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div
                  style={{
                    padding: '20px 0',
                  }}
                >
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div
                  style={{
                    padding: '32px 0',
                  }}
                >
                  <Typography variant='BodyBold'>
                    {t('{{a}} Days', {
                      a: 90,
                    })}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'center',
                width: '100%',
                justifyContent: 'end',
              }}
            >
              <Typography variant='Body'>
                {t('*Web subscription only')}
              </Typography>
              <Button
                style={{
                  width: 172,
                  height: 48,
                }}
                color='primary'
                onClick={handleMoveToBilling}
              >
                {freetrial?.trialUsed ? t('subscribe') : t('Try 1 Month Free')}
              </Button>
            </div>
          </>
        );
    }
  };

  const mobileMarkup = () => {
    switch (step) {
      case 0:
        return (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '90%',
              }}
            >
              <Typography
                style={{
                  textAlign: 'center',
                }}
                variant='H4'
              >
                {t('Unique Fleet_')}
              </Typography>
            </div>
            <MobileFeatureCardMarkup
              title={featureItems[selectedFeature].title}
              content={featureItems[selectedFeature].content}
              onClickPrev={onClickPrevFeature}
              onClickNext={onClickNextFeature}
            />
            <div>
              <img
                src={featureItems[selectedFeature].image}
                alt={featureItems[selectedFeature].title}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'end',
                width: '100%',
                gap: 12,
                padding: '0px 12px',
              }}
            >
              <Typography
                style={{
                  textAlign: 'right',
                  whiteSpace: 'pre-line',
                }}
                variant='Body'
              >
                {t('Want to unlock_').replace('unlock', 'unlock\n')}
              </Typography>
              <Button
                color='primary'
                style={{
                  width: 96,
                  height: 48,
                }}
                onClick={handleMoveToSubscription}
                variant='contained'
              >
                <Typography variant='SmallBold'>{t('Start Now')}</Typography>
              </Button>
            </div>
          </>
        );
      case 1:
        return (
          <div
            style={{
              margin: '0px 12px',
              width: 'calc(100% - 24px)',
            }}
          >
            <div
              style={{
                width: '100%',
                display: 'flex',
                gap: 8,
                flexDirection: 'column',
                marginBottom: 14,
              }}
            >
              <Typography variant='H1'>{t('Fleet')}</Typography>
              <Typography variant='Small'>
                {t('FLEETA Tracking exclusive_')}
              </Typography>
              <div>
                <div
                  style={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='H6'>{t('$15.99')}</Typography>
                  <Typography variant='Caption'>{t('/ month')}</Typography>
                </div>
                <Typography
                  variant='Small'
                  htmlColor={LightColors.primary['2']}
                >
                  {t('12 USD for extra Cloud Camera')}
                </Typography>
              </div>
              <Typography variant='BodyBold'>
                {t('*Web subscription only')}
              </Typography>
            </div>
            <Button
              style={{
                width: '100%',
                height: 48,
                marginBottom: 14,
              }}
              color='primary'
              onClick={handleMoveToBilling}
            >
              {t('Try 1 Month Free')}
            </Button>
            <div
              style={{
                width: '100%',
                marginBottom: 14,
                borderRadius: 16,
                border: `1px solid ${colors.primary['6']}`,
                display: 'flex',
              }}
            >
              <div
                style={{
                  borderRight: `1px solid ${colors.primary['6']}`,
                  flex: 1,
                  padding: '0 16px',
                }}
              >
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Cloud Cameras per Account')}
                  </Typography>
                </div>
                <div
                  style={{
                    padding: '34px 0',
                  }}
                >
                  <Typography variant='Body'>
                    {t('Users per Account')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Live View per Day')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Remote Video Playback_2')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Connect Dashcam Locally_')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>{t('Cloud Storage')}</Typography>
                </div>
                <div
                  className={classes.textBox}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>
                    {t('Live Event Upload')}
                  </Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('Uploads event_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>{t('Live GPS')}</Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Push Notifications_2')}
                  </Typography>
                </div>
              </div>
              <div
                style={{
                  flex: 0.5,
                  padding: '0 12px',
                }}
              >
                <div
                  style={{
                    padding: '10px 0 16px 0',
                  }}
                >
                  <Typography variant='BodyBold'>999</Typography>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant='BodyBold'>{t('Owner : 1')}</Typography>
                  <Typography variant='BodyBold'>{t('Admin : 10')}</Typography>
                  <Typography variant='BodyBold'>
                    {t('Driver : 3 per camera')}
                  </Typography>
                </div>
                <div
                  style={{
                    padding: '8px 0 8px 0',
                  }}
                >
                  <Typography variant='BodyBold'>{t('Unlimited')}</Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='BodyBold'>{t('Unlimited')}</Typography>
                </div>
                <div
                  style={{
                    padding: '38px 0 30px',
                  }}
                >
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <Typography variant='BodyBold'>
                    {t('15GB per Camera')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='BodyBold'>{t('30 days')}</Typography>
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
              </div>
            </div>
            <div
              style={{
                width: '100%',
                marginBottom: 14,
                borderRadius: 16,
                border: `1px solid ${colors.primary['6']}`,
                display: 'flex',
              }}
            >
              <div
                style={{
                  borderRight: `1px solid ${colors.primary['6']}`,
                  flex: 1,
                  padding: '0 16px',
                }}
              >
                <div
                  className={classes.textBox}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>{t('Voice Call')}</Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('Starts a conversation_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div
                  style={{
                    padding: '10px 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant='Body'>{t('Cloud Battery')}</Typography>
                    <Typography htmlColor={colors.primary['2']} variant='Body'>
                      **{t('Available with_')}
                    </Typography>
                  </div>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('Remotely check_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div
                  className={classes.textBox}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>
                    {t('Fatigue Risk Alert')}
                  </Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('Receive a voice_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>{t('Driving Reports')}</Typography>
                </div>
                <div
                  style={{
                    padding: '32px 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>{t('GPS Tracking')}</Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>{t('View your_')}</Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div
                  className={classes.textBox}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant='Body'>{t('Video on Map')}</Typography>
                  <Tooltip
                    style={{
                      cursor: 'pointer',
                    }}
                    title={
                      <Typography variant='Small'>
                        {t('View speed, event_')}
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon />
                  </Tooltip>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Geofence Setup (Web)_')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Multi Live View (Fleeta Web Viewer)')}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <Typography variant='Body'>
                    {t('Email Notifications')}
                  </Typography>
                </div>
              </div>
              <div
                style={{
                  flex: 0.5,
                  padding: '0 12px',
                }}
              >
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div
                  style={{
                    padding: '32px 0',
                  }}
                >
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div
                  style={{
                    padding: '36px 0',
                  }}
                >
                  <Typography variant='BodyBold'>
                    {t('{{a}} Days', {
                      a: 90,
                    })}
                  </Typography>
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
                <div
                  style={{
                    padding: '16px 0',
                  }}
                >
                  <CheckIcon />
                </div>
                <div
                  style={{
                    padding: '20px 0',
                  }}
                >
                  <CheckIcon />
                </div>
                <div className={classes.textBox}>
                  <CheckIcon />
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div>
      <header className={classes.headerDiv}>
        <Link to='/'>
          <FleetaLogoOnly />
        </Link>
        <Avatars
          style={{ cursor: 'pointer' }}
          ref={profileAnchorRef}
          name={userProfile?.email}
          imgSrc={userProfile?.profilePath}
          onClick={() => {
            setOpenProfileMenu(true);
          }}
        />
        <ProfileMenu
          open={openProfileMenu}
          anchorEl={profileAnchorRef.current}
          placement='bottom-end'
          isShowUpgrade={false}
          isDisableSettings={true}
          onClickAway={() => setOpenProfileMenu(false)}
          onCloseMenu={() => setOpenProfileMenu(false)}
        />
      </header>
      <main className={classes.main}>
        {mobile ? mobileMarkup() : webMarkup()}
      </main>
      {mobile && (
        <div
          style={{
            margin: '24px 32px',
            display: 'flex',
            gap: 24,
          }}
        >
          <a
            href={
              // 한국어 / 일본어 일시 해당 국가의 Terms & Policy로 이동 (KimSH)
              i18n.language === 'ko'
                ? 'https://blackvue.com/ko/terms-conditions-korean/'
                : i18n.language === 'ja'
                ? 'https://blackvue.com/ja/terms-conditions-japan/'
                : 'https://blackvue.com/warranty-terms-conditions/'
            }
            target='_blank'
            rel='noreferrer'
          >
            <Typography
              category='Default'
              variant='Small'
              htmlColor={LightColors.primary['3']}
            >
              {t('Terms & Policies')}
            </Typography>
          </a>
          <a
            href='https://helpcenter.blackvue.com/hc/en-us'
            target='_blank'
            rel='noreferrer'
          >
            <Typography
              category='Default'
              variant='Small'
              htmlColor={LightColors.primary['3']}
            >
              {t('Help')}
            </Typography>
          </a>
        </div>
      )}
    </div>
  );
});

export default NeedPaymentScreen;
